import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';

import { getAllDevices } from 'api/bks';

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";
import { useNavigate } from 'react-router-dom';

import cameraOnline from 'nextus-assets/images/camera_online.png';
import cameraWarning from 'nextus-assets/images/camera_warning.png';
import cameraOffline from 'nextus-assets/images/camera_offline.png';
import { getUserAccessLevel } from 'helper/user';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function setFeatures(group) {
    var features = group.geojson;
    // console.log(group);

    features.forEach(function (feature) {
        feature.properties = {
            group_id: group.group_id,
            name: group.name,
            parent_id: group.parent_id
            // uuids: group.uuids
        };
    });

    return features;
}

function setDeviceFeatures(device) {

    var features = [
        {
            type: "Feature",
            properties: {
                name: device.name,
                uuid: device.uuid,
                site_id: device.site_id,
                serial_no: device.serial_number
            },
            geometry: {
                type: 'Point',
                coordinates: device.position
            }
        }
    ];

    return features;
}

function setDevicePointColour(device, user_role) {
    if (!device.active && getUserAccessLevel(user_role, 'technician')) {
        return '#555555';
    }
    else {
        if (device.connectivity != "online") {
            return "#ff667c";
        }
        else if (device.battery <= 20 && getUserAccessLevel(user_role, 'technician')) {
            return "#edaa00";
        }
        else {
            return "#58ba2d";
        }
    }
}

function ExploringMapDevices({ map, mapPadding, extraPadding, user_role = "viewer", group, selected_group, sidebarOpen, menuToggled, setMenuToggled, deviceStyle = 'desktop' }) {

    const navigate = useNavigate();
    const set_padding = 100;

    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleGroupClick = (e) => {
        e.preventDefault();
        if (group.subgroups.length > 0) {
            navigate("/explore/groups/" + group.group_id);
        }
    }

    const handleDeviceClick = (e) => {
        e.preventDefault();
        navigate("/devices/" + e.features[0].properties.uuid);

    }

    const handleMapClick = (e) => {
        if (!e.defaultPrevented) {
            navigate("/explore/groups");
        }
    }

    const handleMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
        if (getUserAccessLevel(user_role, 'technician')) {
            description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }

    const handleMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        popup.remove();
    }

    useEffect(() => { // initial

        var bounds = bbox({
            "type": "FeatureCollection",
            "features": setFeatures(selected_group)
        });

        // console.log(bounds)
        var polygon = bboxPolygon(bounds);

        if (!sidebarOpen) {
            if (extraPadding) {
                var map_padding = [0, 250];
            }
            else {
                var map_padding = [0, 0];
            }
        }
        else {
            if (extraPadding) {
                var map_padding = [600, 250];
            }
            else {
                var map_padding = [600, 0];
            }
        }

        if (map.current) {
            if (menuToggled) {
                if (deviceStyle == 'mobile') {
                    var padding = {
                        top: set_padding / 2,
                        bottom: set_padding / 2,
                        right: set_padding / 2,
                        left: set_padding / 2,
                    }
                }
                else {
                    var padding = {
                        top: set_padding,
                        bottom: map_padding[1] + set_padding,
                        right: set_padding,
                        left: map_padding[0] + set_padding,
                    }
                }

                map.current.fitBounds(polygon.bbox, {
                    // linear: true,
                    padding: padding,
                    // animation: {
                    // duration: 1500
                    // }
                });
                setMenuToggled(false);
            }
            else {
                map.current.easeTo({
                    padding: {
                        left: map_padding[0],
                        bottom: map_padding[1]
                    },
                    duration: 500
                });
            }
        }
    }, [sidebarOpen]);

    useEffect(() => { //mount
        const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
        });

        if (map.current) {
            var existing_online_image = map.current.hasImage('cameraIconOnline');
            var existing_warning_image = map.current.hasImage('cameraIconWarning');
            var existing_offline_image = map.current.hasImage('cameraIconOffline');

            map.current.loadImage(
                cameraOnline, (error, image) => {
                    if (error) throw error;
                    if (!existing_online_image) {
                        map.current.addImage('cameraIconOnline', image);
                    }
                }
            );
            map.current.loadImage(
                cameraWarning, (error, image) => {
                    if (error) throw error;
                    if (!existing_warning_image) {
                        map.current.addImage('cameraIconWarning', image);
                    }
                }
            );
            map.current.loadImage(
                cameraOffline, (error, image) => {
                    if (error) throw error;
                    if (!existing_offline_image) {
                        map.current.addImage('cameraIconOffline', image);
                    }
                }
            );

            var existing_base_layer = map.current.getLayer('geojson-base');
            var existing_base_source = map.current.getSource('geojson-base');

            if (!existing_base_source) {
                map.current.addSource('geojson-base', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": setFeatures(group)
                    }
                });
            }
            if (!existing_base_layer) {
                map.current.addLayer({
                    id: 'geojson-base',
                    type: 'fill',
                    source: 'geojson-base',
                    paint: {
                        "fill-opacity": 0.1,
                        "fill-color": "black"
                    }
                }).addLayer({
                    id: 'geojson-border-base',
                    type: 'line',
                    source: 'geojson-base',
                    paint: {
                        "line-color": "black",
                        "line-width": 2,
                        "line-opacity": 0.2
                    }
                });

                map.current.on('click', 'geojson-base', handleGroupClick);
            }

            if (group.subgroups.length > 0) {
                group.subgroups.forEach(function (subgroup) {
                    if (subgroup.group_id != selected_group.group_id) {
                        var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                        var existing_group_source = map.current.getSource('geojson-' + subgroup.group_id);

                        if (!existing_group_source) {
                            map.current.addSource('geojson-' + subgroup.group_id, {
                                type: 'geojson',
                                data: {
                                    "type": "FeatureCollection",
                                    "features": setFeatures(subgroup)
                                }
                            });
                        }
                        if (!existing_group_layer) {
                            map.current.addLayer({
                                id: 'geojson-' + subgroup.group_id,
                                type: 'line',
                                source: 'geojson-' + subgroup.group_id,
                                paint: {
                                    "line-color": "black",
                                    "line-width": 4,
                                    "line-opacity": 0.4
                                }
                            });
                        }
                    }
                })

                var existing_selected_group_layer = map.current.getLayer('geojson-' + selected_group.group_id);
                var existing_selected_group_source = map.current.getSource('geojson-' + selected_group.group_id);

                // console.log(selected_group)

                if (!selected_group.all_updated) {
                    var polygon_colour = "#ff667c";
                }
                else if (selected_group.devices_monitored > 0 && getUserAccessLevel(user_role, 'technician')) {
                    var polygon_colour = "#edaa00";
                }
                else {
                    var polygon_colour = "#58ba2d";
                }

                if (!existing_selected_group_source) {
                    map.current.addSource('geojson-' + selected_group.group_id, {
                        type: 'geojson',
                        data: {
                            "type": "FeatureCollection",
                            "features": setFeatures(selected_group)
                        }
                    });
                }
                if (!existing_selected_group_layer) {
                    map.current.addLayer({
                        id: 'geojson-' + selected_group.group_id,
                        type: 'line',
                        source: 'geojson-' + selected_group.group_id,
                        paint: {
                            "line-color": polygon_colour,
                            "line-width": 4,
                            "line-opacity": 0.4
                        }
                    });
                }
            }

            selected_group.devices.forEach(function (device) {
                if (device.active) {
                    var existing_device_layer = map.current.getLayer('geojson-' + device.uuid);
                    var existing_device_source = map.current.getSource('geojson-' + device.uuid);

                    if (['1003', '1004', '1005', '1006', '1007', 'Mercury Carpark (1266)'].indexOf(device.name) > -1 || selected_group.type == "camera") {

                        if (!device.active && getUserAccessLevel(user_role, 'technician')) {
                            var icon_image = 'cameraIconInactive';
                        }
                        else {
                            if (device.connectivity == 'offline') {
                                var icon_image = 'cameraIconOffline';
                            }
                            else if (device.battery <= 20 && getUserAccessLevel(user_role, 'technician')) {
                                var icon_image = 'cameraIconWarning';
                            }
                            else {
                                var icon_image = 'cameraIconOnline';
                            }
                        }

                        if (!existing_device_source) {
                            map.current.addSource('device-' + device.uuid, {
                                type: 'geojson',
                                data: {
                                    type: 'FeatureCollection',
                                    features: setDeviceFeatures(device)
                                }
                            });
                        }

                        if (!existing_device_layer) {
                            map.current.addLayer({
                                id: 'device-' + device.uuid,
                                type: 'symbol',
                                source: 'device-' + device.uuid,
                                layout: {
                                    'icon-image': icon_image,
                                    'icon-size': 0.044
                                }
                            });
                        }
                    }
                    else {
                        if (!existing_device_source) {
                            map.current.addSource('device-' + device.uuid, {
                                type: 'geojson',
                                data: {
                                    "type": "FeatureCollection",
                                    "features": setDeviceFeatures(device)
                                }
                            });
                        }
                        if (!existing_device_layer) {
                            map.current.addLayer({
                                id: 'device-' + device.uuid,
                                type: 'circle',
                                source: 'device-' + device.uuid,
                                paint: {
                                    'circle-color': setDevicePointColour(device, user_role),
                                    'circle-radius': 9,
                                    'circle-stroke-width': 3,
                                    'circle-stroke-color': '#ffffff'
                                }
                            });
                        }
                    }
                    map.current.on('click', 'device-' + device.uuid, handleDeviceClick);
                    map.current.on('mouseenter', 'device-' + device.uuid, handleMouseEnter);
                    map.current.on('mouseleave', 'device-' + device.uuid, handleMouseLeave);
                }
            });

            map.current.on('click', handleMapClick);
        }
    }, []);

    useEffect(() => () => { //unmount

        var existing_base_layer = map.current.getLayer('geojson-base');
        if (existing_base_layer) {
            map.current.removeLayer('geojson-base');
            map.current.removeLayer('geojson-border-base');
            map.current.removeSource('geojson-base');

            map.current.off('click', 'geojson-base', handleGroupClick);
        }

        if (group.subgroups.length > 0) {
            group.subgroups.forEach(function (subgroup) {
                var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                if (existing_group_layer) {
                    map.current.removeLayer('geojson-' + subgroup.group_id);
                    map.current.removeSource('geojson-' + subgroup.group_id);
                }
            });

            var existing_selected_group_layer = map.current.getLayer('geojson-' + selected_group.group_id);
            if (existing_selected_group_layer) {
                map.current.removeLayer('geojson-' + selected_group.group_id);
                map.current.removeSource('geojson-' + selected_group.group_id);
            }
        }

        selected_group.devices.forEach(function (device) {
            var existing_device_layer = map.current.getLayer('device-' + device.uuid);
            // console.log(existing_device_layer);
            if (existing_device_layer) {
                map.current.removeLayer('device-' + device.uuid);
                map.current.removeSource('device-' + device.uuid);

                map.current.off('click', 'device-' + device.uuid, handleDeviceClick);
                map.current.off('mouseenter', 'device-' + device.uuid, handleMouseEnter);
                map.current.off('mouseleave', 'device-' + device.uuid, handleMouseLeave);
            }
        });

        map.current.off('click', handleMapClick);
        popup.remove();

    }, []);
}

ExploringMapDevices.propTypes = {
    map: PropTypes.object,
    mapPadding: PropTypes.array,
    user_role: PropTypes.string,
    group: PropTypes.object,
    selected_group: PropTypes.object,
    sidebarOpen: PropTypes.bool,
    menuToggled: PropTypes.bool,
    setMenuToggled: PropTypes.func,
    extraPadding: PropTypes.bool,
};

export default ExploringMapDevices;